import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { Nullable, TriggeredHook } from "sonobello.utilities.react";

import { ContactResult, ContactResultType } from "../../../types/ContactResult";
import { useObx } from "../../../hooks/useObx";
import { ICalendarService } from "../../../Calendar/types/ICalendar";
import ApiRequestPaths from "../../../constants/ApiRequestPaths";
import INoteContentBuilder from "../../../Notes/types/INoteContentBuilder";
import { IPatient } from "../../../types/Entities/Patient";
import Reservation from "../../../types/Reservation";
import { IUseCreateContactResultProps } from "./useCreateContactResult";

/** An appointment request for the booking process. */
export class AppointmentRequest {
  readonly centerId: string;
  readonly customerId: string;
  /** @remarks Intentionally using "leadId" to support legacy nomenclature. */
  readonly leadId: string;
  readonly services: ICalendarService[];
  readonly startTimeUtc: string;
  readonly endTimeUtc: string;
  readonly note: string;
  readonly slotId: number;
  readonly distanceToCenterInMiles: Nullable<number>;

  constructor(
    centerId: string,
    patientId: string,
    contactId: string,
    service: ICalendarService,
    startTimeUtc: DateTime,
    endTimeUtc: DateTime,
    note: string,
    slotId: number,
    distanceToCenterInMiles: Nullable<number>
  ) {
    this.centerId = centerId;
    this.customerId = patientId;
    this.leadId = contactId;
    this.services = [service];
    this.startTimeUtc = startTimeUtc.toISO();
    this.endTimeUtc = endTimeUtc.toISO();
    this.note = note;
    this.slotId = slotId;
    this.distanceToCenterInMiles = distanceToCenterInMiles;
  }
}

/** The expected result of the appointment confirmation hook. */
export interface IConfirmedAppointmentResponse {
  appointment: { startTimeUtc: string; endTimeUtc: string; id: string };
  contactResult: ContactResult;
}

export interface IUseConfirmAppointmentProps<TNoteProps> {
  /** The properties required to build the appointment note. */
  noteProps: TNoteProps;
  /** The note content builder used to create the appointment note. */
  noteContentBuilder: INoteContentBuilder<TNoteProps>;
  /** The properties of the patient who will attend the confirmed appointment. */
  patient: Pick<IPatient, "id" | "lead" | "dateOfBirth" | "email" | "phoneNumber" | "zipCode">;
  /** The identifier of the session's contact. */
  contactId: string;
  /** The hook which creates the result for the session's contact. */
  useCreateContactResult: TriggeredHook<ContactResult, ContactResult, boolean, IUseCreateContactResultProps>;
}

/** The appointment confirmation request response. */
export interface IAppointmentResponse {
  /** The id of the patient who will attend the confirmed appointment.
   * @remarks Intentionally using "customerId" to support legacy nomenclature.
   */
  customerId: string;
  /** Distance to Center in miles relative to the patient */
  distanceToCenterInMiles: Nullable<number>;
  /** The end time of the confirmed appointment as a UTC ISO. */
  endTimeUtc: string;
  /** The assigned identifier of the created appointment. */
  id: string;
  /** The start time of the confirmed appointment as a UTC ISO. */
  startTimeUtc: string;
}

/** A hook which dispatches the appointment and contact result for creation when triggered and returns the response.
 * @typeParam TNoteProps - The properties required to build the appointment note.
 */
const useConfirmAppointment = <TNoteProps>({
  patient,
  contactId,
  noteContentBuilder,
  noteProps,
  useCreateContactResult
}: IUseConfirmAppointmentProps<TNoteProps>): ReturnType<
  TriggeredHook<IConfirmedAppointmentResponse, Reservation, boolean, IUseConfirmAppointmentProps<TNoteProps>>
> => {
  const [result, setResult] = useState<Nullable<IConfirmedAppointmentResponse>>(null);
  const notePropsRef = useRef(noteProps);

  const {
    res: appointmentResult,
    err: appointmentError,
    loading: appointmentLoading,
    setReq: setAppointmentReq
  } = useObx<IAppointmentResponse, AppointmentRequest>("Post Appointment", { method: "post" });

  const {
    result: contactResult,
    isLoading: isContactResultLoading,
    error: contactResultError,
    execute: triggerPostContactResult
  } = useCreateContactResult({ contactId });

  useEffect(() => {
    if (appointmentResult)
      triggerPostContactResult(
        new ContactResult(
          appointmentResult.payload.customerId,
          patient.lead.id,
          ContactResultType.Booked,
          appointmentResult.payload.id
        )
      );
  }, [appointmentResult]);

  useEffect(() => {
    if (contactResult && appointmentResult)
      setResult({
        appointment: {
          id: appointmentResult.payload.id,
          startTimeUtc: appointmentResult.payload.startTimeUtc,
          endTimeUtc: appointmentResult.payload.endTimeUtc
        },
        contactResult
      });
  }, [contactResult, appointmentResult]);

  useEffect(() => {
    notePropsRef.current = noteProps;
  }, [noteProps]);

  const execute = useCallback((reservation: Reservation) => {
    setAppointmentReq(
      q =>
        q && {
          ...q,
          url: ApiRequestPaths.postAppointmentUrlV1_1(
            contactId,
            patient.id,
            reservation.center.id,
            reservation.slot.id
          ),
          payload: new AppointmentRequest(
            reservation.center.id,
            patient.id,
            contactId,
            reservation.service,
            reservation.slot.startTimeUtc,
            reservation.slot.endTimeUtc,
            noteContentBuilder.buildNoteContent(notePropsRef.current),
            reservation.slot.id,
            reservation.center.distance
          )
        }
    );
  }, []);

  return {
    result,
    execute,
    isLoading: isContactResultLoading || appointmentLoading,
    error: Boolean(contactResultError || appointmentError)
  };
};

export default useConfirmAppointment;
